/* eslint-disable @angular-eslint/prefer-inject */
import { ContainerDataService } from '../infrastructure/containers/container.data.service';
import {
    ContainerCreate,
    ContainerDto,
    ContainerRead,
    ContainerSearchParams,
} from '../entities/container.dto';
import { ContainerStore } from '../infrastructure/containers/container.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ContainerMapper } from '../infrastructure/containers/container.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    IBarcodeSearchFacade,
    IManageEntityRoutes,
    IQueryParams,
    PFWmsEntities,
    SafeAny,
} from '@pf/shared-common';
import { asArraySafe, compareSafe } from '@pf/shared-utility';
import { ContainerStatus, ResourceAuth } from '@control-tower/platform-wms';
import { combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { WarehouseFacade } from './warehouse.facade';
import { Operation } from 'fast-json-patch';
import { AutoIncrementerFacade } from '@pf/platform-core/domain';
import { ContainerTypeFacade } from './type-entities/ContainerTypeFacade';

@Injectable()
export class ContainerFacade
    extends AbstractManageEntityFacade<
        ContainerDto,
        ContainerRead,
        ContainerCreate,
        ContainerSearchParams
    >
    implements IBarcodeSearchFacade
{
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly containerTypeFacade = inject(ContainerTypeFacade);
    private readonly autoIncrementerFacade = inject(AutoIncrementerFacade);
    nameField: keyof ContainerDto = 'lpn';

    get containerDataService(): ContainerDataService {
        return this.dataService as ContainerDataService;
    }

    constructor(
        dataService: ContainerDataService,
        store: ContainerStore,
        mapper: ContainerMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );

        this.listenForRealTimeEvents(PFWmsEntities.Container, {
            processEventsIfNotInStore: false,
        });
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ContainerDto>
    ): Observable<IQueryParams<ContainerDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ContainerCreate) {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    override getById$(
        id: string,
        _parentId?: string,
        withChildContainers?: boolean
    ): Observable<ContainerDto> {
        return super.getById$(id).pipe(
            switchMap(container => {
                return this.withChildContainers(container, withChildContainers);
            })
        );
    }

    override getMany$(
        ids: string[],
        withChildContainers?: boolean
    ): Observable<ContainerDto[]> {
        return super.getMany$(ids).pipe(
            switchMap(containers => {
                return combineLatest(
                    containers.map(container => {
                        return this.withChildContainers(
                            container,
                            withChildContainers
                        );
                    })
                );
            })
        );
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ContainerDto, string>> {
        return { lpn: searchText };
    }

    searchByBarcode$(barcodeText: string): Observable<ContainerDto | null> {
        if (!barcodeText) {
            return of(null);
        }

        return this.getByLpn$(barcodeText);
    }

    generateLpn$(containerTypeCode: string, warehouseId: string) {
        const type = `ContainerType_${containerTypeCode}_${warehouseId}`;
        return this.autoIncrementerFacade.getIncrement(type);
    }

    getByLpn(lpn: string): ContainerDto | null {
        const matches = this.store.filter((c: ContainerRead) => c.lpn === lpn);
        return matches.length > 0 ? this.mapper.toDTO(matches[0]) : null;
    }

    getByLpn$(lpn: string): Observable<ContainerDto | null> {
        if (!lpn) {
            return of(null);
        }

        return this.search$({
            filters: {
                lpn,
            },
            pageIndex: 1,
            pageSize: 1,
        }).pipe(
            map(
                result =>
                    result.data?.find(container =>
                        compareSafe(container.lpn, lpn)
                    ) ?? null
            )
        );
    }

    createContainerByTypeCode$(
        typeCode: string,
        container: ContainerDto
    ): Observable<ContainerDto> {
        return this.containerTypeFacade.getByCode$(typeCode).pipe(
            switchMap(type => {
                container.containerType = type;
                if (!container.lpn && container.warehouse.id) {
                    return this.generateLpn$(
                        typeCode,
                        container.warehouse.id
                    ).pipe(
                        map(lpn => {
                            container.lpn = lpn;
                            return container;
                        })
                    );
                }
                return of(container);
            }),
            switchMap(container => {
                return this.save$(container);
            })
        );
    }

    getUnassignedContainers$() {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    hasParent: false,
                    hasEquipment: false,
                } as SafeAny,
            },
            false
        );
    }

    bulkAdd$(containers: ContainerDto[]) {
        return of(containers).pipe(
            map(containers =>
                containers.map(container => this.mapper.toCreateBody(container))
            ),
            switchMap(containers => {
                return (this.dataService as ContainerDataService).createMany$(
                    containers
                );
            }),
            map(containers => this.mapper.toDTOList(containers)),
            tap(containers => {
                this.updated$.next({
                    entity: containers[0],
                    actionText: 'Containers Added',
                });
            })
        );
    }

    assignChild$(containerId: string, parentId: string) {
        if (!containerId || !parentId) {
            throw new Error(
                'Container ID and Parent ID are required to assign'
            );
        }
        return this.containerDataService
            .assignParentContainer$(containerId, parentId)
            .pipe(
                tap(container =>
                    this.updated$.next({
                        entity: this.mapper.toDTO(container),
                        actionText: 'Assigned',
                    })
                ),
                map(() => true)
            );
    }

    unassignChild$(containerId: string) {
        if (!containerId) {
            throw new Error('Container ID is required to unassign');
        }
        return this.containerDataService
            .unassignParentContainer$(containerId)
            .pipe(
                tap(container =>
                    this.updated$.next({
                        entity: this.mapper.toDTO(container),
                        actionText: 'Unassigned',
                    })
                ),
                map(() => true)
            );
    }

    getChildContainers$(containerId: string) {
        return this.searchAll$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                parentContainerId: containerId,
            },
        });
    }

    setContainerStatus$(containerId: string, status: ContainerStatus) {
        return this.getById$(containerId).pipe(
            switchMap(container => {
                if (container.status === status) {
                    return of(container);
                }
                container.status = status;
                return this.save$(container);
            })
        );
    }

    assignSlot$(containerId: string, slotId: string) {
        return this.containerDataService
            .assignSlot$(containerId, slotId)
            .pipe(map(container => this.mapper.toDTO(container)));
    }

    private withChildContainers(
        container: ContainerDto,
        withChildContainers?: boolean
    ) {
        if (!withChildContainers) {
            return of(container);
        }
        return this.getChildContainers$(container.id).pipe(
            map(childContainers => {
                container.children = asArraySafe(childContainers).filter(
                    child => !child.isDeleted && child.id !== container.id
                );
                return container;
            })
        );
    }

    removeEquipment$(containerId: string) {
        return this.dataService
            .update$(
                containerId,
                [
                    {
                        op: 'replace',
                        value: null,
                        path: '/equipmentId',
                    } as Operation,
                ],
                null as SafeAny
            )
            .pipe(
                tap(entity => {
                    this.store.update(entity);
                }),
                map(container => this.mapper.toDTO(container)),
                tap(container => {
                    this.updated$.next({
                        entity: container,
                        actionText: 'removed equipment',
                    });
                })
            );
    }

    setEquipment$(containerId: string, equipmentId: string) {
        return this.dataService
            .update$(
                containerId,
                [
                    {
                        op: 'replace',
                        value: equipmentId,
                        path: '/equipmentId',
                    } as Operation,
                    {
                        op: 'remove',
                        path: '/slotId',
                    },
                ],
                null as SafeAny
            )
            .pipe(
                tap(entity => {
                    this.store.update(entity);
                }),
                map(container => this.mapper.toDTO(container)),
                tap(container => {
                    this.updated$.next({
                        entity: container,
                        actionText: 'assigned equipment',
                    });
                })
            );
    }
}
